import React, { useState, useMemo, useEffect } from "react";
import { Popover, Button, Calendar, ConfigProvider } from "antd";
import { CalendarOutlined, LeftOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/ar";
import enUS from "antd/locale/en_US";
import frFR from "antd/locale/fr_FR";
import arEG from "antd/locale/ar_EG";
import { useTranslation } from "react-i18next";
import CustomButton from "components/Common/ui/CustomButton";

dayjs.extend(localeData);

interface CalendarFilterProps {
  onDateSelect: (date: dayjs.Dayjs | null) => void;
  tasks?: { date: string }[];
}

const antdLocaleMap: Record<"en" | "fr" | "ar", typeof enUS> = {
  en: enUS,
  fr: frFR,
  ar: arEG,
};

const dateFormatMap: Record<"en" | "fr" | "ar", string> = {
  en: "MM-DD-YYYY",
  fr: "DD-MM-YYYY",
  ar: "DD-MM-YYYY",
};

const CalendarFilter: React.FC<CalendarFilterProps> = ({ onDateSelect, tasks = [] }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  // derive "en" | "fr" | "ar"
  const baseLang = useMemo(
    () => (i18n.language.startsWith("fr") ? "fr" : i18n.language.startsWith("ar") ? "ar" : "en"),
    [i18n.language]
  );

  // apply Day.js locale so month/weekday names localize
  useEffect(() => {
    dayjs.locale(baseLang);
  }, [baseLang]);

  const antdLocale = useMemo(() => antdLocaleMap[baseLang], [baseLang]);
  const dateFormat = useMemo(() => dateFormatMap[baseLang], [baseLang]);
  const direction = baseLang === "ar" ? "rtl" : "ltr";

  const taskDatesSet = useMemo(() => {
    return new Set(tasks.map(task => dayjs(task.date).format("YYYY-MM-DD")));
  }, [tasks]);

  const handleSelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
    setOpen(false);
    onDateSelect(date);
  };

  const dateCellRender = (date: dayjs.Dayjs) => {
    const formatted = date.format("YYYY-MM-DD");
    if (taskDatesSet.has(formatted)) {
      return <span className="calendar-task-dot" />;
    }
    return null;
  };

  const handleReset = () => {
    setSelectedDate(null);
    onDateSelect(null);
    setOpen(false);
  };

  // prefer i18n months; fallback to Day.js locale data
  const monthsFromI18n = t("calendar.months", { returnObjects: true }) as string[] | undefined;
  const months =
    Array.isArray(monthsFromI18n) && monthsFromI18n.length === 12
      ? monthsFromI18n
      : dayjs.months(); // uses current dayjs locale

  const calendarContent = (
    <ConfigProvider locale={antdLocale} direction={direction}>
      <div style={{ width: 280, padding: 4, margin: 10 }}>
        <Calendar
          fullscreen={false}
          onSelect={handleSelect}
          dateCellRender={dateCellRender}
          headerRender={({ value, onChange }) => {
            const year = value.year();
            const month = value.month();
            const prev = () => onChange(value.clone().subtract(1, "month"));
            const next = () => onChange(value.clone().add(1, "month"));

            return (
              <div style={{ display: "flex", justifyContent: "space-between", padding: 8 }}>
                <Button type="text" size="small" icon={<LeftOutlined />} onClick={prev} aria-label={t("calendar.prev")} />
                <span>{`${months[month]} ${year}`}</span>
                <Button type="text" size="small" icon={<RightOutlined />} onClick={next} aria-label={t("calendar.next")} />
              </div>
            );
          }}
        />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "8px 0 0" }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>
            {t("reset")} 
          </Button>
        </div>
      </div>
    </ConfigProvider>
  );

  return (
    <div>
      <Popover
        content={calendarContent}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <CustomButton variant="primary">
          <CalendarOutlined style={{ marginRight: 8 }} />
          {selectedDate
            ? `${t("tasks_for")} ${selectedDate.format(dateFormat)}`
            : t("select_a_date")}
        </CustomButton>
      </Popover>
    </div>
  );
};

export default CalendarFilter;
